import React, { useEffect, useRef, useState } from 'react'
import type { PlayerAPI, ErrorEvent as BmErrorEvent } from 'bitmovin-player'
import type { UIManager } from 'bitmovin-player-ui'
import styles from './StreamPlayer.module.scss'

const PLAYER_KEY = import.meta.env.VITE_BITMOVIN_PLAYER_KEY as string | undefined

/**
 * Adaptive streaming formats this player decodes. Progressive files (MP4) are
 * out of scope by design — they play off a plain video element and need neither
 * MSE nor a license, so routing them here would put a working path behind one.
 */
export type StreamFormat = 'HLS' | 'DASH'

export interface StreamPlayerError {
  /** Editor-facing description of what went wrong. */
  message: string
  /** Bitmovin ErrorCode, when the failure came from the player itself. */
  code?: number
  name?: string
}

export interface StreamPlayerProps {
  /** Manifest URL — an .m3u8 for HLS, an .mpd for DASH. */
  url: string
  format: StreamFormat
  /** Browsers block autoplay with sound, so this only starts when also muted. */
  autoplay?: boolean
  muted?: boolean
  /** Layered over the default 16:9 stage, for callers that size their own box. */
  className?: string
  /** Reports a fatal failure so the caller can offer its own fallback. */
  onError?: (error: StreamPlayerError) => void
}

// Bitmovin ErrorCodes restated for an editor: "the domain isn't licensed" and
// "the origin sent no CORS header" are both actionable, a bare number isn't.
// Unmapped codes keep the raw code and name so they stay lookup-able.
function describeError(code?: number, name?: string): string {
  const suffix = code ? ` (Bitmovin ${code}${name ? ` ${name}` : ''})` : ''
  if (code !== undefined && code >= 2000 && code < 2100) {
    return `This stream is DRM-protected and the CMS holds no license token.${suffix}`
  }
  switch (code) {
    case 1103:
      return `Bitmovin rejected the license key — check VITE_BITMOVIN_PLAYER_KEY is the key this environment should use.${suffix}`
    case 1104:
    case 1105:
      return `This domain is not on the Bitmovin license allowlist — add the CMS origin in the Bitmovin dashboard.${suffix}`
    case 1202:
      return `The manifest loaded but could not be parsed.${suffix}`
    case 1203:
    case 1204:
    case 1205:
    case 1206:
      return `No playback technology available in this browser supports this stream.${suffix}`
    case 1207:
      return `A media segment came back empty, so the stream may be incomplete.${suffix}`
    case 1208:
      return `The manifest could not be loaded. Most often a missing Access-Control-Allow-Origin header on the origin, or a 403/404 — check the Network tab.${suffix}`
    case 1210:
      return `The HLS stream failed during playback.${suffix}`
    case 1211:
      return `The stream uses an encryption method that needs DRM configuration the CMS does not have.${suffix}`
    case 1401:
      return `Timed out downloading the manifest.${suffix}`
    case 1402:
      return `Timed out downloading a media segment.${suffix}`
    default:
      return `Playback failed.${suffix}`
  }
}

type Status = { kind: 'loading' } | { kind: 'ready' } | { kind: 'no-key' }

/**
 * Bitmovin-backed player for an adaptive stream, live or on demand. Both formats
 * run through one player: SourceConfig takes `hls` and `dash` as sibling keys,
 * so the only per-format difference is which key carries the URL.
 */
const StreamPlayer: React.FC<StreamPlayerProps> = ({
  url,
  format,
  autoplay = false,
  muted = false,
  className,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const onErrorRef = useRef(onError)
  // destroy() only finishes asynchronously, and a finishing instance clears
  // shared state on the container it was mounted in. When the URL changes
  // without a remount, the next instance has to wait for that to complete.
  const teardownRef = useRef<Promise<unknown>>(Promise.resolve())
  const [status, setStatus] = useState<Status>({ kind: 'loading' })

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    if (!PLAYER_KEY) {
      setStatus({ kind: 'no-key' })
      return
    }

    setStatus({ kind: 'loading' })

    let disposed = false
    let player: PlayerAPI | null = null
    let ui: UIManager | null = null

    const fail = (code?: number, name?: string) => {
      if (disposed) return
      onErrorRef.current?.({ message: describeError(code, name), code, name })
    }

    void (async () => {
      await teardownRef.current
      if (disposed) return

      // Imported in-effect so the ~MB SDK is a lazy chunk rather than part of
      // the CMS entry bundle — most editors never open a page that plays video.
      let playerModule: typeof import('bitmovin-player')
      let uiModule: typeof import('bitmovin-player-ui')
      try {
        // The UI stylesheet is pulled in here rather than at module scope so it
        // rides the lazy chunk; a static import lands ~139KB of it in the CMS's
        // main stylesheet, on every page, for every editor.
        ;[playerModule, uiModule] = await Promise.all([
          import('bitmovin-player'),
          import('bitmovin-player-ui'),
          import('bitmovin-player-ui/dist/css/bitmovinplayer-ui.css'),
        ])
      } catch {
        fail(undefined, 'player bundle failed to load')
        return
      }
      if (disposed) return

      const { Player, PlayerEvent } = playerModule
      try {
        player = new Player(container, {
          key: PLAYER_KEY,
          // Chrome is attached below via UIFactory rather than the inline `ui`
          // option so a teardown can release it before destroying the player.
          ui: false,
          playback: { autoplay, muted },
        })
      } catch (err) {
        fail(undefined, err instanceof Error ? err.message : 'player setup failed')
        return
      }

      // The effect may have been cleaned up while the SDK was loading; the
      // player exists now, so tear it down rather than leaking it.
      if (disposed) {
        void player.destroy()
        player = null
        return
      }

      player.on(PlayerEvent.Error, (event: BmErrorEvent) => {
        fail(event.code, event.name)
      })

      ui = uiModule.UIFactory.buildUI(player)

      try {
        await player.load(format === 'HLS' ? { hls: url } : { dash: url })
        if (!disposed) setStatus({ kind: 'ready' })
      } catch (err) {
        // load() makes no guarantee about what it rejects with — the DASH path
        // rejects with the raw HTTP response, carrying no code. The Error event
        // above is the reliable source, so this is only a backstop for when it
        // never fires; callers should prefer whichever report carries a code.
        const e = err as { code?: number; name?: string }
        fail(typeof e?.code === 'number' ? e.code : undefined, e?.name)
      }
    })()

    return () => {
      disposed = true
      const dyingPlayer = player
      const dyingUi = ui
      player = null
      ui = null
      // Publish the teardown so the next instance in this container waits on it
      // rather than racing a half-destroyed predecessor. Swallowed because a
      // teardown failure is not actionable and must not surface unhandled.
      teardownRef.current = (async () => {
        // Release the UI before destroying the player: destroy() tears down
        // attached UI too, but release() is UIManager's documented teardown.
        dyingUi?.release()
        if (dyingPlayer) await dyingPlayer.destroy()
      })().catch(() => undefined)
    }
  }, [url, format, autoplay, muted])

  if (status.kind === 'no-key') {
    return (
      <div className={styles.notice}>
        <p>VITE_BITMOVIN_PLAYER_KEY is not set, so {format} cannot be played here.</p>
        <p className={styles.noticeHint}>
          Add it to <code>apps/cms/.env</code> — see <code>env.example</code>.
        </p>
      </div>
    )
  }

  return (
    <div className={className ? `${styles.stage} ${className}` : styles.stage}>
      <div ref={containerRef} className={styles.stageInner} />
      {status.kind === 'loading' && (
        <div className={styles.overlay}>Loading {format} stream…</div>
      )}
    </div>
  )
}

export default StreamPlayer
